// A refinery — a still that cooks raw wood into fuel for trucks. A riveted
// boiler tank under a tall condenser column with a looping copper pipe, a stout
// chimney, and a glowing fire-box at its foot. Ported 1:1 from age-of-mcp's
// refinery.ts: the shared foundation/scaffold come from the library, the boiler
// and still are local helpers, and the four build stages are transcribed from
// stage(s).
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, benchWork, smokePlume, firePulse, EFFECT_LOOP, type Part } from "../../lib";

const STONE = "#5a5a5a";
const TANK = "#6f7a82"; // riveted steel boiler
const COPPER = "#a9683a"; // condenser column & pipes
const CHIM = "#3c3a38"; // chimney
const SMOKE = "#cfcfcf";
const FIRE = "#ff8a2a"; // fire-box glow
const BAND = "#43484c"; // tank bands

const BASE = 0.14;

// The boiler tank + its bands.
function boiler(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.2, 0.22, 0.4, 12), color: TANK, position: [-0.06, BASE + 0.2, 0.02] },
    { geometry: new THREE.CylinderGeometry(0.205, 0.205, 0.03, 12), color: BAND, position: [-0.06, BASE + 0.1, 0.02] },
    { geometry: new THREE.CylinderGeometry(0.205, 0.205, 0.03, 12), color: BAND, position: [-0.06, BASE + 0.32, 0.02] },
    // Glowing fire-box mouth at the foot.
    { geometry: new THREE.BoxGeometry(0.16, 0.1, 0.04), color: FIRE, position: [-0.06, BASE + 0.06, 0.22], name: "firebox" },
  ];
}

// The "still is running" cue: smoke rising from the chimney mouth (the chimney
// tops out at y = BASE + 0.55) while the fire-box glow pulses.
const plume = smokePlume([-0.24, BASE + 0.56, -0.18], { rise: 0.3, radius: 0.045, color: SMOKE });

// The condenser column + a looping pipe back to the tank, and a chimney.
function still(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.07, 0.08, 0.6, 10), color: COPPER, position: [0.2, BASE + 0.3, -0.05] },
    { geometry: new THREE.CylinderGeometry(0.085, 0.085, 0.05, 10), color: BAND, position: [0.2, BASE + 0.6, -0.05] },
    // Pipe arching from the tank top to the column.
    { geometry: new THREE.CylinderGeometry(0.025, 0.025, 0.3, 6), color: COPPER, position: [0.07, BASE + 0.46, -0.02], rotation: [0, 0, Math.PI / 2.4] },
    // Chimney with rising smoke (the puffs the "active" animation drives).
    { geometry: new THREE.BoxGeometry(0.1, 0.42, 0.1), color: CHIM, position: [-0.24, BASE + 0.34, -0.18] },
    ...plume.parts,
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.5)];
  if (s === 1) return [foundationSlab(0.5), { geometry: new THREE.CylinderGeometry(0.2, 0.22, 0.12, 12), color: TANK, position: [-0.06, BASE + 0.06, 0.02] }, ...scaffold(0.44, 0.5)];
  if (s === 2) return [foundationSlab(0.5), ...boiler(), ...scaffold(0.44, 0.8)];
  return [
    { geometry: new THREE.BoxGeometry(0.5, BASE, 0.5), color: STONE, position: [0, BASE / 2, 0] },
    ...boiler(),
    ...still(),
  ];
}

export const refinery = defineModel({
  id: "refinery",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Refinery",
  tags: ["building", "refinery", "low-poly", "age-of-mcp", "classic", "animated"],
  summary: "Low-poly refinery.",
  description: "A still that cooks raw wood into fuel.",
  author: "age-of-mcp",
  footprint: 0.42,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Still running",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [...plume.tracks, firePulse("firebox", [-0.06, BASE + 0.06, 0.22])],
    },
  ],
  stations: [
    { id: "still", position: [-0.06, 0, 0.42], facing: Math.PI, description: "Where a worker tends the still." },
  ],
  interactions: [
    { id: "refine", label: "At the still", station: "still", variants: ["complete"], ...benchWork() },
  ],
});
